import { useCallback, useEffect, useMemo } from 'react';

import { buildHotelPlannerUrl } from '../../utils/url';
import {
    HotelPlannerCustomSurfaceParam,
    HotelPlannerTracking,
} from '../Analytics/constants';
import { trackEvent } from '../Analytics/TrackedClick';
import { HpEventCtaCustom5ByVariant } from '../GlobalPrivileges/FindAccommodations/constants';
import useGlobalPrivilegesCta from './useGlobalPrivilegesCta';

const trackedImpressions = new Set();

const getEventCityName = (event) => {
    const location = event?.eventLocations?.[0];
    if (!location) {
        return '';
    }
    return location.cityName || location.placeName || location.googleFormattedName || '';
};

const useHpEventCta = ({ surface, event, variant }) => {
    const gpCta = useGlobalPrivilegesCta({ surface, variant });

    const hotelPlannerUrl = useMemo(
        () => buildHotelPlannerUrl(event, {
            custom4: HotelPlannerCustomSurfaceParam[surface],
            custom5: HpEventCtaCustom5ByVariant[variant],
        }),
        [event, surface, variant],
    );

    const eventCityName = useMemo(() => getEventCityName(event), [event]);

    useEffect(() => {
        const impressionKey = `${HotelPlannerTracking.EVENT_LOCATION_MODULE_IMPRESSION}_${event?.id ?? 'noid'}_${surface}`;
        if (trackedImpressions.has(impressionKey)) {
            return;
        }
        trackEvent(HotelPlannerTracking.EVENT_LOCATION_MODULE_IMPRESSION, {
            event_id: event?.id,
            surface,
            variant,
        });
        trackedImpressions.add(impressionKey);
    }, [event?.id, surface, variant]);

    const handleSearchHotelsClick = useCallback((e) => {
        if (e) {
            e.stopPropagation();
        }
        trackEvent(HotelPlannerTracking.HOTEL_PLANNER_SEARCH_CLICK, {
            event_id: event?.id,
            surface,
            variant,
            referringSurface: HotelPlannerTracking.FIND_ACCOMMODATIONS_SURFACE,
        });
        if (hotelPlannerUrl) {
            window.open(hotelPlannerUrl, '_blank', 'noopener,noreferrer');
        }
    }, [hotelPlannerUrl, event?.id, surface, variant]);

    return {
        ...gpCta,
        eventCityName,
        handleSearchHotelsClick,
        hotelPlannerUrl,
    };
};

export default useHpEventCta;
